import { createRouter, createWebHistory } from 'vue-router'
import emitter, { AppEvents } from '@/lib/events'
import { OAuthFlowTypeEnum } from '@/lib/app.model'
import {
	RouteNotFound,
	RouteSignUp,
	RouteLogin,
	RouteOAuth,
	RouteOAuthSignup,
	RouteOAuthService,
	RouteOAuthProceed,
	RouteOAuthComplete,
	RouteOAuthSocialLoginCallback,
	RouteOAuthIntegration,
	RouteOAuthLink,
	RouteRecover,
	RouteVerify,
	RouteAccess,
	RouteProfile,
	RouteChangePassword,
	RouteChangeEmail,
	RouteLoginOptions,
	RouteConfirmDeleteAccount,
	RouteConfirmEmail,
	RouteSignupConfirmEmail,
	RouteAccount,
	RouteAccountSettings,
	RouteSetEmail,
	RouteApp,
	RouteApps,
} from './routes'
import {
	memberAccessGuard,
	oauthFlowGuard,
	requiredQueryParamsGuard,
	redirectToProfileGuard,
} from './guards'
import { AuthFlowPayloadIntent } from '@/store/state'

const routes = [
	{
		path: '/:pathMatch(.*)*',
		name: RouteNotFound,
		component: () => import('../views/NotFound.vue')
	},
	{
		path: '/',
		redirect: () => {
			return { name: RouteLogin }
		}
	},
	{
		path: '/signup',
		name: RouteSignUp,
		component: () => import('../views/SignUp.vue'),
		beforeEnter: [ redirectToProfileGuard ],
	},
	{
		path: '/login',
		name: RouteLogin,
		component: () => import('../views/Login.vue'),
		beforeEnter: [ redirectToProfileGuard ],
	},
	{
		path: '/oauth',
		name: RouteOAuth,
		component: () => import('../views/OAuth.vue'),
		props: {
			type: OAuthFlowTypeEnum.Basic
		}
	},
	{
		path: '/oauth/signup',
		name: RouteOAuthSignup,
		component: () => import('../views/OAuth.vue'),
		props: {
			type: OAuthFlowTypeEnum.Basic,
			intent: AuthFlowPayloadIntent.SIGNUP,
		}
	},
	{
		path: '/oauth/service',
		name: RouteOAuthService,
		component: () => import('../views/OAuth.vue'),
		props: {
			type: OAuthFlowTypeEnum.Service
		}
	},
	{
		path: '/oauth/integration',
		name: RouteOAuthIntegration,
		component: () => import('../views/OAuth.vue'),
		props: {
			type: OAuthFlowTypeEnum.Integration
		}
	},
	{
		path: '/oauth/link',
		name: RouteOAuthLink,
		component: () => import('../views/OAuth.vue'),
		props: {
			type: OAuthFlowTypeEnum.Link
		}
	},
	{
		path: '/oauth/proceed',
		name: RouteOAuthProceed,
		component: () => import('../views/OAuthProceed.vue')
	},
	{
		path: '/oauth/complete',
		name: RouteOAuthComplete,
		component: () => import('../views/OAuthComplete.vue'),
		beforeEnter: [ oauthFlowGuard ],
	},
	{
		path: '/oauth/social-login-callback/:source',
		name: RouteOAuthSocialLoginCallback,
		component: () => import('../views/OAuthCallback.vue'),
		props: route => ({
			source: route.params.source,
			type: OAuthFlowTypeEnum.Basic,
		}),
	},
	{
		path: '/recover',
		name: RouteRecover,
		component: () => import('../views/Recover.vue')
	},
	{
		path: '/verify',
		name: RouteVerify,
		component: () => import('../views/Verify.vue'),
		beforeEnter: [ requiredQueryParamsGuard([ 'id' ]) ]
	},
	{
		path: '/access',
		name: RouteAccess,
		component: () => import('../views/Access.vue'),
		beforeEnter: [ memberAccessGuard ],
	},
	{
		path: '/profile',
		name: RouteProfile,
		component: () => import('../views/Profile.vue'),
		beforeEnter: [ memberAccessGuard ],
	},
	{
		path: '/change-password',
		name: RouteChangePassword,
		component: () => import('../views/ChangePassword.vue'),
		beforeEnter: [ memberAccessGuard ],
	},
	{
		path: '/change-email',
		name: RouteChangeEmail,
		component: () => import('../views/ChangeEmail.vue'),
		beforeEnter: [ memberAccessGuard ],
	},
	{
		path: '/login-with',
		name: RouteLoginOptions,
		component: () => import('../views/LoginOptions.vue'),
		beforeEnter: [ oauthFlowGuard ],
	},
	{
		path: '/confirm-email',
		name: RouteConfirmEmail,
		component: () => import('../views/ConfirmEmail.vue')
	},
	{
		path: '/confirm-delete-account',
		name: RouteConfirmDeleteAccount,
		component: () => import('../views/ConfirmDeleteAccount.vue')
	},
	{
		path: '/signup-confirm-email',
		name: RouteSignupConfirmEmail,
		component: () => import('../views/SignupConfirmEmail.vue'),
		beforeEnter: [ requiredQueryParamsGuard([ 'email' ]) ]
	},
	{
		path: '/account',
		name: RouteAccount,
		component: () => import('../views/Account.vue'),
		beforeEnter: [ memberAccessGuard, oauthFlowGuard ],
	},
	{
		path: '/account-settings',
		name: RouteAccountSettings,
		component: () => import('../views/AccountSettings.vue'),
		beforeEnter: [ memberAccessGuard ],
	},
	{
		path: '/set-email',
		name: RouteSetEmail,
		component: () => import('../views/SetEmail.vue'),
		beforeEnter: [ memberAccessGuard ],
	},
	{
		path: '/app/:id',
		name: RouteApp,
		component: () => import('../views/App.vue'),
		beforeEnter: [ memberAccessGuard ],
		props: true,
	},
	{
		path: '/apps',
		name: RouteApps,
		component: () => import('../views/Apps.vue'),
		beforeEnter: [ memberAccessGuard ],
	},
]

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes
})

router.afterEach(() => {
	emitter.emit(AppEvents.HideToast)
})

export default router
